<script setup>
import { computed } from 'vue';
import { useSocialShare, buildShareLink, isHttpShareLink } from '/app/composables/socialShare';

const props = defineProps({
    /** Name of the social network to display (e.g. 'twitter', 'facebook', 'imgur'). */
    network: {
        type: String,
        required: true,
    },
    /** URL of the content to share. */
    url: {
        type: String,
        required: true,
    },
    /** Title of the content to share. */
    title: {
        type: String,
        required: true,
    },
    /** Description of the content to share. */
    description: {
        type: String,
        default: '',
    },
    /** Quote, used for Facebook. */
    quote: {
        type: String,
        default: '',
    },
    /** Comma-separated hashtags, used for Twitter and Facebook. */
    hashtags: {
        type: String,
        default: '',
    },
    /** Twitter handle, used for Twitter. */
    twitterUser: {
        type: String,
        default: '',
    },
    /** Media URL, used for Pinterest. */
    media: {
        type: String,
        default: '',
    },
    /** HTML tag used for the rendered element. */
    tag: {
        type: String,
        default: 'a',
    },
    /** Popup window size. */
    popup: {
        type: Object,
        default: () => ({ width: 626, height: 436 }),
    },
    /**
     * Optional gate that runs before the share/touch action. Receives `(next, provider)`
     * — call `next()` to proceed with the share, or skip it to handle the action yourself.
     */
    beforeShare: {
        type: Function,
        default: (next) => next(),
    },
});

const emit = defineEmits(['open', 'close', 'change', 'failure']);

const { share, touch } = useSocialShare();

const content = computed(() => ({
    url: props.url,
    title: props.title,
    description: props.description,
    quote: props.quote,
    hashtags: props.hashtags,
    twitterUser: props.twitterUser,
    media: props.media,
}));

const key = computed(() => props.network.toLowerCase());

function doShare() {
    share(props.network, content.value, {
        width: props.popup.width,
        height: props.popup.height,
        onOpen: (k, u) => emit('open', k, u),
        onClose: (k, u) => emit('close', k, u),
        onChange: (k, u) => emit('change', k, u),
        onFailure: (k, u) => emit('failure', k, u),
    });
}

function doTouch() {
    touch(props.network, content.value, {
        onOpen: (k, u) => emit('open', k, u),
    });
}

function onClick() {
    const link = buildShareLink(props.network, content.value);
    const action = isHttpShareLink(link) ? doShare : doTouch;

    props.beforeShare(action, key.value);
}
</script>

<template>
    <component :is="tag" :class="`share-network-${key}`" :href="tag === 'a' ? 'javascript:void(0)' : undefined" @click="onClick">
        <slot />
    </component>
</template>
